/**
 * SignupsTable - Paginated signups table with actions
 */
import { ChevronLeft, ChevronRight, List, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getThemeByKey, LEGACY_CAMPAIGN_THEMES } from './campaignUtils';

export default function SignupsTable({
  report,
  signups,
  selectedCampaign,
  viewingAllSignups,
  loadingSignups,
  signupsPagination,
  promoStats,
  deletingCode,
  onViewAllSignups,
  onShowRecentOnly,
  onPageChange,
  onDeleteSignupCode,
  onClearAllPromoCodes
}) {
  const displaySignups = (selectedCampaign || viewingAllSignups) ? signups : report?.recent_signups || [];

  return (
    <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-xl text-foreground">
            {selectedCampaign 
              ? `Campaign ${selectedCampaign} Signups` 
              : viewingAllSignups 
                ? 'All Signups' 
                : 'Recent Signups'
            }
          </h2>
          {(selectedCampaign || viewingAllSignups) && signupsPagination.total_count > 0 && (
            <span className="text-sm text-muted-foreground">
              ({signupsPagination.total_count} total)
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {promoStats && (
            <div className="flex items-center gap-4 text-sm mr-4">
              <span className="text-muted-foreground">
                Codes: <span className="text-foreground font-medium">{promoStats.used_codes}</span> / {promoStats.total_codes} used
              </span>
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                onClick={onClearAllPromoCodes}
              >
                Clear All Codes
              </Button>
            </div>
          )}
          {!selectedCampaign && !viewingAllSignups && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onViewAllSignups(1)}
              className="rounded-full"
              data-testid="view-all-signups-btn"
            >
              <List className="w-4 h-4 mr-1" />
              View All Signups
            </Button>
          )}
          {(selectedCampaign || viewingAllSignups) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onShowRecentOnly}
            >
              Show Recent Only
            </Button>
          )}
        </div>
      </div>

      {loadingSignups ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="signups-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Phone</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Country</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Promo Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Campaign</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displaySignups.map((signup, i) => {
                  const campaignData = report?.campaigns?.find(c => c.campaign_id === signup.campaign_id);
                  const themeKey = campaignData?.theme || 'teal';
                  const themeOption = getThemeByKey(themeKey);
                  const theme = LEGACY_CAMPAIGN_THEMES[signup.campaign_id] || {
                    gradient: themeOption.gradient,
                    color: themeOption.textColor,
                    bg: themeOption.bg,
                    border: themeOption.border
                  };
                  return (
                    <tr key={signup.id || i} className="border-b border-border hover:bg-muted/50">
                      <td className="py-3 px-4 text-sm text-foreground font-medium">{signup.name}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        <span className="font-mono">
                          {signup.country_code && signup.phone_local 
                            ? `${signup.country_code} ${signup.phone_local}`
                            : signup.phone
                          }
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {signup.country_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{signup.email}</td>
                      <td className="py-3 px-4">
                        {signup.coupon_code ? (
                          <code className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded font-mono font-bold tracking-wider">
                            {signup.coupon_code}
                          </code>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">No code</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${theme?.bg} ${theme?.color}`}>
                          Campaign {signup.campaign_id}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {signup.created_at ? new Date(signup.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {signup.coupon_code && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                            onClick={() => onDeleteSignupCode(signup.id)}
                            disabled={deletingCode === signup.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {displaySignups.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-muted-foreground">
                      No signups yet. Share the QR codes to start collecting leads!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {(selectedCampaign || viewingAllSignups) && signupsPagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border" data-testid="pagination-controls">
              <div className="text-sm text-muted-foreground">
                Showing {((signupsPagination.current_page - 1) * signupsPagination.per_page) + 1} - {Math.min(signupsPagination.current_page * signupsPagination.per_page, signupsPagination.total_count)} of {signupsPagination.total_count} signups
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(signupsPagination.current_page - 1)}
                  disabled={!signupsPagination.has_prev}
                  className="rounded-full"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1 px-3">
                  <span className="text-sm font-medium text-foreground">
                    Page {signupsPagination.current_page}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    of {signupsPagination.total_pages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(signupsPagination.current_page + 1)}
                  disabled={!signupsPagination.has_next}
                  className="rounded-full"
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
